import { Loader2 } from 'lucide-react';
import { useMemo } from 'react';

import { AtlasIcon } from '@crossnokaye/ux-animatics';
import { getInitials, UserAvatar } from '@crossnokaye/ux-common';

import { NavOptions } from '@/components/navigation/constants';
import { AccessSelectorContent } from '@/components/navigation/left/components/shared/access-selector-content';
import { NavTooltipContent } from '@/components/navigation/left/components/shared/nav-tooltip-content';
import { useAgentProxyMode } from '@/hooks/use-agent-proxy-mode';
import { useNavStore } from '@/stores/nav';
import { Popover, PopoverContent, PopoverTrigger } from '@/ux-common/components/popover/popover';
import { Tooltip, TooltipTrigger } from '@/ux-common/components/tooltip/tooltip';

import { CollapsedMainNav } from './collapsed-main-nav';
import { FacilityMoreMenu } from './components/facility-more-menu';

/**
 * Collapsed navigation for the left side of the navigation bar.
 */
export const CollapsedNav = ({
  isViewOnly,
  toggleExpanded,
  onSelect,
  onSignOut,
  onToggleActive,
}: {
  isViewOnly: boolean;
  toggleExpanded: () => void;
  onSelect: (id: string) => void;
  onSignOut: () => void;
  onToggleActive: () => void;
}) => {
  const { userName, userAccessIsLoading, activeNavMenu } = useNavStore();
  const { isAgentProxyMode } = useAgentProxyMode();

  const handleSignOut = () => {
    if (!onSignOut) return;
    onSignOut();
  };

  const handleToggleActive = () => {
    if (!onToggleActive) return;
    onToggleActive();
  };

  const initials = useMemo(() => getInitials(userName ?? ''), [userName]);

  return (
    <div className={'flex flex-col h-full'} data-testid={'collapsed-nav'} onClick={toggleExpanded}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* No onClick of its own: the click bubbles to the rail's
              toggleExpanded — a second handler here fired it twice and the
              nav snapped straight back shut. */}
          <div
            className={'flex justify-center p-4 cursor-pointer hover:bg-popover'}
            data-testid={'collapsed-nav-logo'}
          >
            <AtlasIcon symbol={'atlasLogo'} size={'2rem'} />
          </div>
        </TooltipTrigger>
        <NavTooltipContent label={'Click to Expand Menu'} />
      </Tooltip>
      {/* Its tooltip lives inside FacilityMoreMenu, which takes no ref — an
          outer TooltipTrigger would have nothing to attach to. */}
      <FacilityMoreMenu />
      {activeNavMenu === NavOptions.Main ? <CollapsedMainNav onSelect={onSelect} /> : null}
      {!isAgentProxyMode && (
        <div
          className={'flex justify-center p-4 align-bottom mt-auto cursor-pointer'}
          onClick={(e) => e.stopPropagation()}
        >
          <Popover>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger data-testid={'collapsed-nav-access-selector'}>
                  {userAccessIsLoading ? (
                    <Loader2 className="glyph glyph-lg animate-spin" aria-label="Loading" />
                  ) : (
                    <UserAvatar
                      initials={initials}
                      indicatorClass={isViewOnly ? 'bg-warning' : 'bg-primary'}
                      className={'bg-popover hover:bg-input'}
                    />
                  )}
                </PopoverTrigger>
              </TooltipTrigger>
              <NavTooltipContent label={'Click to View Access Options'} />
            </Tooltip>

            {/* The registry popover draws no surface, so this one brings its own:
                the look the package popover painted, until design specifies one. */}
            <PopoverContent
              side={'top'}
              className={'w-72 max-w-max rounded-md bg-popover text-popover-foreground shadow-md'}
              data-testid={'collapsed-nav-access-selector-menu-content'}
            >
              <AccessSelectorContent
                isViewOnly={isViewOnly}
                handleToggleActive={handleToggleActive}
                handleSignOut={handleSignOut}
              />
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
};
